import { useEffect, useMemo, useRef, useState } from 'react';
import { matchesToReveal, OUTREACH_SEARCH_REVEAL_STEP_MS } from 'fym-shared/src/music24/outreach/live';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { usePrefersReducedMotion } from '../../promote/themes/usePrefersReducedMotion';
import type { OutreachLiveMatch } from '../outreachTypes';

const STORAGE_PREFIX = 'm24.outreach.searchSeen';

function readLastSeen(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLastSeen(key: string, iso: string): void {
  try {
    window.localStorage.setItem(key, iso);
  } catch {
    // A private window or blocked storage: the next view animates the rows again.
  }
}

/**
 * The paced reveal of the real matches (design t1): the rows that Plug found after the last view
 * of this person on this campaign appear one at a time, `OUTREACH_SEARCH_REVEAL_STEP_MS` apart.
 * The rows seen before show at once. The hook never adds a row or a number: it only holds back
 * real rows for a moment. With `prefers-reduced-motion` every row shows at once.
 */
export function useSearchReveal(campaignUuid: string, matches: OutreachLiveMatch[]) {
  const userId = useUserTokenDecoded()?.id ?? 0;
  const key = `${STORAGE_PREFIX}.${userId}.${campaignUuid}`;
  const reducedMotion = usePrefersReducedMotion();
  // The last view before this one: read once for each key, so new rows of this view animate too.
  const baseline = useRef<{ key: string; lastSeenAt: string | null } | null>(null);
  const known = useRef(new Set<string>());
  const queue = useRef<string[]>([]);
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  const [justFound, setJustFound] = useState<ReadonlySet<string>>(() => new Set());
  const [queueSize, setQueueSize] = useState(0);

  useEffect(() => {
    if (baseline.current?.key !== key) {
      baseline.current = { key, lastSeenAt: readLastSeen(key) };
    }
    const animate = new Set(matchesToReveal(matches, baseline.current.lastSeenAt).map((match) => match.targetUuid));
    const now: string[] = [];
    matches.forEach((match) => {
      if (known.current.has(match.targetUuid)) {
        return;
      }
      known.current.add(match.targetUuid);
      if (reducedMotion || !animate.has(match.targetUuid)) {
        now.push(match.targetUuid);
      } else {
        queue.current.push(match.targetUuid);
      }
    });
    if (reducedMotion && queue.current.length > 0) {
      now.push(...queue.current);
      queue.current = [];
    }
    if (now.length > 0) {
      setRevealed((prev) => new Set([...prev, ...now]));
    }
    setQueueSize(queue.current.length);
  }, [key, matches, reducedMotion]);

  useEffect(() => {
    if (queueSize === 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const next = queue.current.shift();
      setQueueSize(queue.current.length);
      if (next !== undefined) {
        setRevealed((prev) => new Set([...prev, next]));
        setJustFound((prev) => new Set([...prev, next]));
      }
    }, OUTREACH_SEARCH_REVEAL_STEP_MS);
    return () => window.clearTimeout(timer);
  }, [queueSize, revealed]);

  // Remember the newest row that this person saw (not the clock: a row found while the screen
  // was open but not yet shown still animates on the next view).
  useEffect(() => {
    let newest = 0;
    matches.forEach((match) => {
      const at = Date.parse(match.foundAt);
      if (revealed.has(match.targetUuid) && at > newest) {
        newest = at;
      }
    });
    if (newest > 0) {
      writeLastSeen(key, new Date(newest).toISOString());
    }
  }, [key, matches, revealed]);

  const visible = useMemo(() => matches.filter((match) => revealed.has(match.targetUuid)), [matches, revealed]);
  return { visible, justFound };
}
